import { FilterState } from "store/features/filter/filterSlice";

import { predicateSourceMigration } from "utils/filter";

import { LegendItemModel } from "models";
import { IPredicate, IQuery } from "models/filter";

/** Checked values that each need a bin of their own; an Exclude and the Others row don't. */
export function checkedBinCount(items: LegendItemModel[], property: string): number {
  return items.filter(
    (item) => item.property === property && !item.isNotEqual && !item.isOthersRollup
  ).length;
}

/** MaxBins arrives as a number or as text; empty or below 1 means no cap. */
export function exceedsMaxBins(
  maxBins: Maybe<number | string>,
  checkedCount: number
): boolean {
  const cap = Number(maxBins);
  return cap >= 1 && checkedCount > cap;
}

function addCheckedTitles(
  predicates: Maybe<IPredicate[]>,
  property: string,
  titles: Set<string>
): void {
  for (const predicate of predicates ?? []) {
    if (
      predicate.property !== property ||
      predicate.rollup ||
      predicate.negatedOperator
    ) {
      continue;
    }
    // A predicate saved before sourceKey existed is named the way the legend names it.
    titles.add(predicate.sourceKey || predicateSourceMigration(predicate));
  }
}

/** Values checked on `property` in the filter: each is its own legend predicate. */
export function checkedValueTitles(
  propertiesFilter: Maybe<FilterState["propertiesFilter"]>,
  property: string
): Set<string> {
  const titles = new Set<string>();
  for (const filter of propertiesFilter ?? []) {
    addCheckedTitles(filter.predicates, property, titles);
  }
  return titles;
}

/** Whether the filter holds any legend predicate on `property`: a check, an Exclude or Others. */
export function hasPredicateOn(
  propertiesFilter: Maybe<FilterState["propertiesFilter"]>,
  property: string
): boolean {
  return (propertiesFilter ?? []).some((filter) =>
    filter.predicates?.some((predicate) => predicate.property === property)
  );
}

export function checkedValueCount(
  propertiesFilter: Maybe<FilterState["propertiesFilter"]>,
  property: string
): number {
  return checkedValueTitles(propertiesFilter, property).size;
}

/** The same count over a query sent to the filter service, wherever its predicates sit. */
export function checkedValueCountInQuery(query: Maybe<IQuery>, property: string): number {
  const titles = new Set<string>();
  const visit = (node: Maybe<IQuery>) => {
    if (!node) return;
    addCheckedTitles(node.predicates, property, titles);
    node.children_nodes?.forEach(visit);
  };
  visit(query);
  return titles.size;
}
